import type { ComponentType } from 'react'
import { matchRoutes, type RouteObject } from 'react-router'

import type { Product } from '@/config/product'

type RouteComponents = Record<string, ComponentType>

const bundled = new Map<Product, RouteComponents>()

/**
 * Routes a product's pages from components already imported, instead of loading them lazily.
 *
 * Tests call it so a page renders at once. The app loads each product's pages with the first of
 * its routes to open, so neither product's visitors download the other's.
 */
export function bundleRouteComponents(product: Product, components: RouteComponents): void {
  bundled.set(product, components)
}

/** Route properties for one product's components: the component once bundled, otherwise a lazy load. */
export function routeComponents<Components extends RouteComponents>(
  product: Product,
  load: () => Promise<Components>
) {
  return (name: keyof Components & string): Pick<RouteObject, 'Component' | 'lazy'> => {
    const components = bundled.get(product)
    if (components) return { Component: components[name] }
    return { lazy: async () => ({ Component: (await load())[name] }) }
  }
}

/**
 * Loads the lazy routes `location` matches, before the router renders it.
 *
 * A prerendered page has to hydrate into the markup it arrived with, which a route still loading
 * would render as nothing.
 */
export async function loadRouteModules(routes: RouteObject[], location: string): Promise<void> {
  const matches = matchRoutes(routes, location) ?? []
  await Promise.all(
    matches.map(async ({ route }) => {
      if (typeof route.lazy !== 'function') return
      Object.assign(route, await route.lazy(), { lazy: undefined })
    })
  )
}
